import BidSearch from "@/modules/bids/BidSearch.model";
import BidsService from "@/modules/bids/BidsService";
import {store} from "@/plugins/vuex";
import {BIDS_TABLE_NAME, SOURCE_BIDS_TABLE} from "@/modules/constants";
import i18n from "@/shared/i18n";
import _ from "lodash";

let getDefaultTableFields = () => {
  return [
    {key: "title", label: i18n.t('Title'), sortable: true, isVisible: true, expand: 'lot.inventory'},
    {key: "amount", label: i18n.t('Amount'), sortable: true, isVisible: true},
    {key: "date", label: i18n.t('Date'), sortable: true, isVisible: true},
    {key: "type", label: i18n.t('Bid Type'), sortable: true, isVisible: true},
    {key: "is_winner", label: i18n.t('Winner'), sortable: true, isVisible: true},

    {key: "bidder", label: i18n.t('Bidder'), sortable: true, isVisible: false, expand: 'bidder.customer'},
    {key: "email", label: i18n.t('Email'), sortable: false, isVisible: false, expand: 'bidder.customer'},
    {key: "phone", label: i18n.t('Phone'), sortable: false, isVisible: false, expand: 'bidder.customer'},
    {key: "stock_no", label: i18n.t('Stock'), sortable: true, isVisible: false, expand: 'lot.inventory'},
    {key: "sale_no", label: i18n.t('Sale'), sortable: true, isVisible: false, expand: 'lot.auction'},
    {key: "lot_no", label: i18n.t('Lot'), sortable: true, isVisible: false},
    {key: "actions", label: i18n.t('Actions'), sortable: true, exclude_from_export: true, isVisible: false, expand: 'is_latest'},
    {
      key: 'created_data',
      label: i18n.t('Created at / Created by'),
      sortable: false,
      tdClass: 'created-data-col',
      isVisible: false,
      expand: 'created_by'
    },
  ]
}

let getDefaultTableData = () => {
  let tableData = {
    loading: false,
    rows: [],
    pagination: {
      limit: 50,
      page: 1,
      total: 0,
    },
    ordering: '-created_at',
    search: '',
    searchModel: new BidSearch(),
    sale: null,
    extraFilters: {},
    extraExpands: '',
    expand: 'bidder',
    toggleColumns: {
      fields: getDefaultTableFields(),
      requiredFields: ['title', 'amount'],
      defaultFields: ['title', 'amount', 'date', 'type', 'is_winner'],
    }
  }

  if (store && store.state.global) {
    if (store.state.global.enableContinuousAuction) {
      tableData.toggleColumns.fields = tableData.toggleColumns.fields.filter(field => field.key !== 'sale_no')
    }
  }

  return tableData
};

export default {
  namespaced: true,
  state: {
    tableSource: null,
    tableData: {
      sale: getDefaultTableData(),
      bids: getDefaultTableData(),
      negotiationModal: getDefaultTableData(),
      reservationView: getDefaultTableData(),
      customer: getDefaultTableData(),
    },
    modal: {
      show: false,
      object: null,
      sale: null
    },
  },
  actions: {
    initTableData({state}) {
      state.tableData = {
        sale: getDefaultTableData(),
        bids: getDefaultTableData(),
        negotiationModal: getDefaultTableData(),
        reservationView: getDefaultTableData(),
        customer: getDefaultTableData(),
      }
      return true
    },
    showModal({commit}, {object = null, source, sale} = {}) {
      commit('showModal', {object: _.cloneDeep(object), source, sale});
    },
    hideModal({commit}) {
      commit('hideModal');
    },
    resetTableDataPaginationPage({state}) {
      state.tableData[state.tableSource].pagination.page = 1;
    },
    async updateTableSource({state}, {
      extraFilters, source, sale, extraExpands, ordering = false, clearBeforeUse = false
    }) {
      state.tableSource = source;
      if (clearBeforeUse) {
        state.tableData[source].rows = [];
        state.tableData[source].sale = null;
        state.tableData[source].pagination = {
          limit: 50,
            page: 1,
            total: 0,
        }
      }
      state.tableData[source].extraFilters = extraFilters;
      state.tableData[source].extraExpands = extraExpands;
      state.tableData[source].sale = sale;
      state.tableData[source].ordering = ordering ? ordering : state.tableData[source].ordering;
      return true
    },
    async getTableParams({state}, payload = null) {
      let data;
      let currentTableData = state.tableData[state.tableSource];
      const ordering = await store.dispatch('global/getTableDataOrdering', {
        tableData: currentTableData,
        tableName: BIDS_TABLE_NAME
      });

      if (payload) {
        data = {...payload.pagination, search: payload.search, ordering: payload.ordering,};
      } else {
        data = {...currentTableData.pagination, search: currentTableData.search, ordering: ordering}
      }

      if (currentTableData.searchModel) {
        const searchModelData = currentTableData.searchModel.toJSON();
        data = {...data, ...searchModelData}
      }

      if (currentTableData.extraFilters) {
        data = {...data, ...currentTableData.extraFilters};
      }

      const defaultExpand = currentTableData.expand
      const expand = currentTableData.toggleColumns.fields
        .filter(c => currentTableData.toggleColumns.defaultFields.includes(c.key) && c.expand && !defaultExpand.includes(c.expand))
        .map(c => c.expand)

      if (defaultExpand) {
        expand.push(defaultExpand)
      }
      if (currentTableData.extraExpands) {
        expand.push(currentTableData.extraExpands)
      }

      data.expand = expand.join(',');
      return data;
    },
    async getTableData({commit, dispatch}, payload) {
      let params = await dispatch('getTableParams', payload);

      commit('updateTableLoading', true);
      const response = await BidsService.getBids(params);
      commit('updateTableLoading', false);

      if (response.success) {
        commit('updateTableData', {
          rows: response.body.results,
          pagination: {limit: params.limit, page: params.page, total: response.body.count},
          ordering: params.ordering,
          search: params.search
        });
      }
      return response;
    },
    updateSearchModel({commit, state}, searchModel) {
      commit('updateSearchModel', new BidSearch({
        ...state.tableData[state.tableSource].searchModel,
        ...searchModel
      }));
    },
    async resetTable({state, dispatch}) {
      const extraFilters = state.tableData[state.tableSource].extraFilters;
      const extraExpands = state.tableData[state.tableSource].extraExpands;
      const tmpToggleColumns = _.cloneDeep(state.tableData[state.tableSource].toggleColumns);
      const sale = state.tableData[state.tableSource].sale;
      let defaultTableData = getDefaultTableData();
      defaultTableData.pagination.limit = state.tableData[state.tableSource].pagination.limit;
      state.tableData[state.tableSource] = defaultTableData;
      state.tableData[state.tableSource].extraFilters = extraFilters;
      state.tableData[state.tableSource].extraExpands = extraExpands;
      state.tableData[state.tableSource].toggleColumns = tmpToggleColumns;
      state.tableData[state.tableSource].sale = sale;
      dispatch('getTableData');
    },
    async updateWinnerBid({dispatch}, payload) {
      const response = await BidsService.updateWinnerBid(payload);
      if (response.success) {
        dispatch('getTableData');
      }
      return response
    },
    async deleteLatestBid({dispatch}, payload) {
      const response = await BidsService.deleteLatestBid(payload);
      if (response.success) {
        dispatch('getTableData');
      }
      return response
    },
    async createBid({dispatch, state}, payload) {
      const response = await BidsService.createBid(payload);
      if (response.success && state.modal.source === SOURCE_BIDS_TABLE) {
        dispatch('getTableData');
      }
      return response
    },
    async updateToggleColumns({commit, dispatch}, payload) {
      if (payload.isSaveAction) {
        commit('updateTableLoading', true);
        await commit('updateToggleColumns', payload.selectedColumns);
        await dispatch('getTableData');
        commit('updateTableLoading', false);
      } else {
        await commit('updateToggleColumns', payload.selectedColumns);
      }
    },

  },
  mutations: {
    updateTableData: (state, payload) => state.tableData[state.tableSource] = {...state.tableData[state.tableSource], ...payload},
    updateTableLoading: (state, payload) => state.tableData[state.tableSource].loading = payload,
    updateSearchModel: (state, searchModel) => state.tableData[state.tableSource].searchModel = searchModel,
    showModal: (state, {object, source, sale}) => {
      state.modal.show = true;
      state.modal.object = object;
      state.modal.source = source;
      state.modal.sale = sale;
    },
    hideModal: state => state.modal.show = false,
    updateToggleColumns: (state, selectedColumns) => {
      state.tableData[state.tableSource].toggleColumns.defaultFields = [...selectedColumns];
      let selectedFields = [];
      let notSelectedFields = [];
      selectedColumns.forEach(s => {
        selectedFields.push({
          ...state.tableData[state.tableSource].toggleColumns.fields.find(f => f.key === s),
          isVisible: true
        });
      });
      state.tableData[state.tableSource].toggleColumns.fields.forEach(f => {
        if (!selectedColumns.includes(f.key)) {
          notSelectedFields.push({...f, isVisible: false});
        }
      });
      state.tableData[state.tableSource].toggleColumns.fields = [...selectedFields, ...notSelectedFields];
    },
  }
}
